/**
 * @author Jorge Casas
 * @creationDate 10/10/2025
 * @path src\components\dashboard\ChartCostCentersTotal.js
 */

import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import { translate } from 'react-i18next';
import PropTypes from 'prop-types';

import { Doughnut } from 'react-chartjs-2';
import * as Common from 'common/components';
import Chart from 'chart.js';
import 'chartjs-plugin-datalabels';

if (!Chart.__doughnutPatchesApplied) {
  Chart.__doughnutPatchesApplied = true;

  Chart.plugins.register({
    beforeDatasetDraw(chart) {
      if (chart.config.type !== 'doughnut') return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.08)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 6;
      ctx.shadowOffsetX = 0;
    },
    afterDatasetDraw(chart) {
      if (chart.config.type !== 'doughnut') return;
      chart.ctx.restore();
    },
  });

  Chart.plugins.register({
    beforeDatasetsDraw(chart) {
      if (chart.config.type !== 'doughnut') return;
      const opts = chart.config.options || {};
      if (!opts.__clearChartAreaBG) return;
      const ca = chart.chartArea;
      if (!ca) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.clearRect(ca.left, ca.top, ca.right - ca.left, ca.bottom - ca.top);
      ctx.restore();
    },
  });

  Chart.plugins.register({
    afterDatasetsDraw(chart) {
      if (chart.config.type !== 'doughnut') return;
      const opts = chart.config.options.centerText;
      if (!opts || !opts.display) return;

      const ctx = chart.chart.ctx;
      const w = chart.chart.width,
        h = chart.chart.height;
      const text = typeof opts.text === 'function' ? opts.text(chart) : opts.text || '';
      const lines = Array.isArray(text) ? text : String(text).split('\n');

      const fontSize = opts.fontSize || 16;
      ctx.save();
      ctx.fillStyle = opts.color || '#334155';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font =
        (opts.fontWeight || '600') +
        ' ' +
        fontSize +
        'px ' +
        (opts.fontFamily ||
          'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial');

      const cx = w / 2,
        cy = h / 2;
      const lh = (opts.lineHeight || 1.2) * fontSize;
      const startY = cy - ((lines.length - 1) * lh) / 2;
      for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i], cx, startY + i * lh);
      ctx.restore();
    },
  });
}

const GOLDEN_ANGLE = 137.50776405003785;
const SAT = 0.68;
const LIG = 0.58;

function hash(s) {
  let h = 0,
    str = String(s || '');
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
function hslToRgb(h, s, l) {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s,
      p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
function colorFromKey(key, alpha = 0.92, variant = 0) {
  const baseHue = (hash(key) * GOLDEN_ANGLE) % 360;
  const hue = ((baseHue + variant * 17) % 360) / 360;
  const [r, g, b] = hslToRgb(hue, SAT, LIG);
  return `rgba(${r},${g},${b},${alpha})`;
}
function rgbaToArray(c) {
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([.\d]+))?\)/.exec(c || '');
  return m ? [+m[1], +m[2], +m[3], m[4] ? +m[4] : 1] : null;
}
function lightenRgba(c, t) {
  const a = rgbaToArray(c);
  if (!a) return c;
  const r = Math.round(a[0] + (255 - a[0]) * t),
    g = Math.round(a[1] + (255 - a[1]) * t),
    b = Math.round(a[2] + (255 - a[2]) * t);
  return `rgba(${r},${g},${b},${a[3]})`;
}
function compact(n) {
  try {
    return new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(
      n
    );
  } catch (_) {
    return String(n);
  }
}

const MAX_SLICES = 14;
const MIN_PCT = 0.015;
const CHART_HEIGHT = 300;

class ChartCostCentersTotal extends Component {
  static propTypes = { data: PropTypes.array.isRequired, t: PropTypes.func };

  state = { mode: 'pct' };

  shouldComponentUpdate = (nProps, nState) =>
    !isEqual(nProps.data, this.props.data) || !isEqual(nState, this.state);

  toKeyStr(v) {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
      if ('label' in v) return String(v.label);
      if ('name' in v) return String(v.name);
      if ('key' in v) return String(v.key);
    }
    return String(v);
  }

  prepare(items) {
    const t = this.props.t || ((v) => v);
    const rows = (items || [])
      .map((d) => ({
        key: this.toKeyStr(d && d.name),
        label: t(this.toKeyStr(d && d.name)),
        total: (d && d.total) || 0,
      }))
      .filter((r) => r.total > 0);

    const total = rows.reduce((s, r) => s + r.total, 0);
    if (!total) return { total: 0, rows: [], legend: [] };

    const sorted = rows.sort((a, b) => b.total - a.total);
    const keep = [];
    let others = 0;
    for (let i = 0; i < sorted.length; i++) {
      const r = sorted[i],
        p = r.total / total;
      if (i < MAX_SLICES - 1 || p >= MIN_PCT) keep.push(r);
      else others += r.total;
    }
    if (others > 0)
      keep.push({ key: '__OTHERS__', label: t('common.others') || 'Otros', total: others });

    const legend = keep.map((r) => ({
      label: r.label,
      value: r.total,
      pct: Math.round((r.total / total) * 1000) / 10,
      color: r.key === '__OTHERS__' ? 'rgba(100,116,139,0.6)' : colorFromKey(r.key, 1),
    }));

    return { total, rows: keep, legend };
  }

  chartRef = null;
  setChartRef = (ref) => {
    this.chartRef = ref ? ref.chartInstance || ref.chart || ref : null;
  };
  downloadPNG = () => {
    const c = this.chartRef;
    if (!c || !c.toBase64Image) return;
    const a = document.createElement('a');
    a.href = c.toBase64Image();
    a.download = 'total_por_centros.png';
    a.click();
  };

  getData = (canvas) => {
    const ctx = canvas && typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
    const prepared = this.prepare(this.props.data);
    const labels = [],
      values = [],
      bg = [],
      hover = [],
      stroke = [];

    for (let i = 0; i < prepared.rows.length; i++) {
      const r = prepared.rows[i];
      const base = r.key === '__OTHERS__' ? 'rgba(100,116,139,0.6)' : colorFromKey(r.key, 0.92);
      const hov = r.key === '__OTHERS__' ? 'rgba(100,116,139,0.9)' : colorFromKey(r.key, 1, 1);
      let fill = base;
      if (ctx && canvas) {
        const grad = ctx.createLinearGradient(0, 0, 0, canvas.height || CHART_HEIGHT);
        grad.addColorStop(0, lightenRgba(base, 0.28));
        grad.addColorStop(1, base);
        fill = grad;
      }
      labels.push(r.label);
      values.push(r.total);
      bg.push(fill);
      hover.push(hov);
      stroke.push(base.replace(/, ?0\.\d+\)$/, ', 1)'));
    }

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: bg,
          hoverBackgroundColor: hover,
          borderColor: stroke,
          borderWidth: 2,
        },
      ],
    };
  };

  renderLegend(prepared) {
    const items = prepared.legend;
    if (!items.length) return null;
    return (
      <div
        style={{
          marginTop: 8,
          maxHeight: 180,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
          gap: '8px 16px',
          paddingRight: 6,
        }}
      >
        {items.map((it, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: it.color,
                marginRight: 8,
                flex: '0 0 auto',
              }}
            />
            <span
              style={{
                color: '#334155',
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {it.label}
            </span>
            <span style={{ marginLeft: 'auto', color: '#64748B', fontSize: 12 }}>
              {compact(it.value)} · {it.pct}%
            </span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const t = this.props.t || ((v) => v);
    const prepared = this.prepare(this.props.data);
    const hasData = prepared.total > 0;

    const totalAll = prepared.total;
    const centros = (this.props.data || []).length;
    const ordered = (this.props.data || [])
      .slice()
      .sort((a, b) => ((b && b.total) || 0) - ((a && a.total) || 0));
    const top1 = ordered[0];
    const topShare = top1 ? (((top1.total || 0) / Math.max(totalAll, 1)) * 100).toFixed(1) : '0.0';

    return (
      <div className="col-lg-6">
        <Common.Portlet title={t('dashboard.totalByCostCenters')}>
          {/* Toolbar */}
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap chart-toolbar">
            <div className="d-flex align-items-center flex-wrap">
              <span className="badge badge-light mr-2 mb-2">
                Total:{' '}
                {typeof totalAll === 'number' && Number.prototype.toLocaleString
                  ? totalAll.toLocaleString('es-MX')
                  : String(totalAll)}
              </span>
              <span className="badge badge-light mr-2 mb-2">Centros: {centros}</span>
              <span className="badge badge-light mr-2 mb-2">Top concentra: {topShare}%</span>
            </div>

            <div className="d-flex align-items-center flex-wrap mt-2 mt-sm-0" style={{ gap: 10 }}>
              <div
                className="segmented-toggle mb-2"
                role="group"
                aria-label="Modo de visualización"
              >
                <button
                  type="button"
                  className={'segmented-btn ' + (this.state.mode === 'abs' ? 'is-active' : '')}
                  aria-pressed={this.state.mode === 'abs'}
                  onClick={() => this.setState({ mode: 'abs' })}
                  title="Mostrar valores absolutos"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M5 9h14M5 15h14M9 3l-2 18M17 3l-2 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>ABS</span>
                </button>

                <button
                  type="button"
                  className={'segmented-btn ' + (this.state.mode === 'pct' ? 'is-active' : '')}
                  aria-pressed={this.state.mode === 'pct'}
                  onClick={() => this.setState({ mode: 'pct' })}
                  title="Mostrar porcentaje del total"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M19 5L5 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle cx="7.5" cy="6.5" r="2" stroke="currentColor" strokeWidth="2" />
                    <circle cx="16.5" cy="17.5" r="2" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>%</span>
                </button>
              </div>

              <button
                type="button"
                className="download-btn--pro mb-2"
                aria-label="Descargar gráfico en PNG"
                onClick={this.downloadPNG}
                title="Descargar imagen PNG"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M12 3v10m0 0l-4-4m4 4l4-4M5 21h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="btn-text" aria-hidden="true">
                  Descargar PNG
                </span>
              </button>
            </div>
          </div>

          <div style={{ padding: '8px 6px' }}>
            <div
              className="doughnut-container"
              style={{ height: CHART_HEIGHT, position: 'relative' }}
            >
              {hasData ? (
                <Doughnut
                  ref={this.setChartRef}
                  data={(c) => this.getData(c)}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutoutPercentage: 58,
                    rotation: -Math.PI / 2,
                    legend: { display: false },

                    __clearChartAreaBG: true,

                    layout: { padding: { top: 45, right: 24, bottom: 56, left: 32 } },

                    onHover: (e) => {
                      e.target.style.cursor = 'default';
                    },
                    onClick: () => {},

                    tooltips: {
                      backgroundColor: 'rgba(17,24,39,0.95)',
                      cornerRadius: 8,
                      xPadding: 12,
                      yPadding: 10,
                      displayColors: false,
                      callbacks: {
                        title: (tt, data) => {
                          const i = tt && tt[0] ? tt[0].index : 0;
                          return (data.labels && data.labels[i]) || '';
                        },
                        label: (tt, data) => {
                          const vals =
                            (data.datasets && data.datasets[0] && data.datasets[0].data) || [];
                          const val = vals[tt.index] || 0;
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = Math.round((val / sum) * 1000) / 10;
                          return this.state.mode === 'pct'
                            ? ' ' + p + '%'
                            : ' ' +
                                (Number.prototype.toLocaleString
                                  ? val.toLocaleString('es-MX')
                                  : String(val)) +
                                ' (' +
                                p +
                                '%)';
                        },
                      },
                    },

                    plugins: {
                      datalabels: {
                        display: true,
                        clip: true,
                        clamp: true,
                        font: { weight: '600', size: 11 },
                        formatter: (value, ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (value / sum) * 100;
                          if (this.state.mode === 'pct')
                            return (p >= 10 ? p.toFixed(0) : p.toFixed(1)) + '%';
                          if (value >= 1000) {
                            try {
                              return new Intl.NumberFormat('es-MX', {
                                notation: 'compact',
                                maximumFractionDigits: 1,
                              }).format(value);
                            } catch (_) {
                              return String(value);
                            }
                          }
                          return String(value);
                        },
                        anchor: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? 'end' : 'center';
                        },
                        align: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? 'end' : 'center';
                        },
                        offset: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          let off = p < 4 ? 12 : 0;
                          try {
                            const chart = ctx.chart;
                            const meta = chart.getDatasetMeta(ctx.datasetIndex);
                            const el = meta && meta.data && meta.data[ctx.dataIndex];
                            const ca = chart.chartArea;
                            if (el && el._model && ca) {
                              const a = (el._model.startAngle + el._model.endAngle) / 2;
                              const dy = Math.sin(a);
                              if (dy < -0.7) off = Math.max(4, off - 4);
                            }
                          } catch (_) {}
                          return off;
                        },
                        color: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? '#0f172a' : '#fff';
                        },
                        backgroundColor: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? 'rgba(255,255,255,0.95)' : null;
                        },
                        borderColor: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? 'rgba(15,23,42,0.08)' : null;
                        },
                        borderWidth: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? 1 : 0;
                        },
                        borderRadius: 6,
                        padding: (ctx) => {
                          const vals = ctx.dataset.data || [];
                          const sum = vals.reduce((a, b) => a + (+b || 0), 0) || 1;
                          const p = (vals[ctx.dataIndex] / sum) * 100;
                          return p < 4 ? 8 : 0;
                        },
                      },
                    },

                    centerText: {
                      display: true,
                      text: () => {
                        const total = (this.props.data || []).reduce(
                          (s, d) => s + ((d && d.total) || 0),
                          0
                        );
                        const txt =
                          typeof total === 'number' && Number.prototype.toLocaleString
                            ? total.toLocaleString('es-MX')
                            : String(total);
                        return ['TOTAL', txt];
                      },
                      fontSize: 14,
                      fontWeight: '600',
                      color: '#334155',
                      lineHeight: 1.3,
                    },
                  }}
                />
              ) : (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    opacity: 0.7,
                  }}
                >
                  {t('common.noData') || 'Sin datos para mostrar'}
                </div>
              )}
            </div>

            {this.renderLegend(prepared)}
          </div>
        </Common.Portlet>
      </div>
    );
  }
}

export default translate()(ChartCostCentersTotal);



// WEBPACK FOOTER //
// src/components/dashboard/ChartCostCentersTotal.js